import { useEffect, useRef, useState } from 'react';
import { normalizeTagName } from '@qkindex/contracts';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
} from '@mui/material';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogContent } from '../../components/ui/DialogContent';
import { DialogActions } from '../../components/ui/DialogActions';
import { manageSharing, ShareTag } from './sharingService';

export function CreateTagDialog({
  collectionId,
  tags,
  onClose,
  onBack,
  onPendingChange,
  onCreated,
}: {
  collectionId: string;
  tags: ShareTag[];
  onClose: () => void;
  onBack: () => void;
  onPendingChange?: (pending: boolean) => void;
  onCreated: (tag: ShareTag) => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    onPendingChange?.(saving);
    return () => onPendingChange?.(false);
  }, [saving, onPendingChange]);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const trimmed = name.trim();
  const validation =
    trimmed.length > 200
      ? 'Use 200 characters or fewer.'
      : tags.some(
            (tag) =>
              tag.state === 'active' &&
              normalizeTagName(tag.name) === normalizeTagName(trimmed),
          )
        ? 'A tag with this name already exists in this collection.'
        : tags.filter((tag) => tag.state === 'active').length >= 200
          ? 'This collection has reached its 200-tag limit.'
          : '';
  const close = () => {
    if (!pending.current) onClose();
  };
  const create = async () => {
    if (pending.current || !trimmed || validation) return;
    pending.current = true;
    setSaving(true);
    setError('');
    try {
      const result = await manageSharing(collectionId, 'saveTag', {
        name: trimmed,
      });
      if (!result.tagId)
        throw new Error('Unable to create tag. Please try again.');
      if (alive.current)
        onCreated({
          tagId: result.tagId,
          name: trimmed,
          state: 'active',
          revision: 0,
        });
    } catch (failure) {
      if (alive.current)
        setError(
          failure instanceof Error
            ? failure.message
            : 'Unable to create tag. Please try again.',
        );
    } finally {
      pending.current = false;
      if (alive.current) setSaving(false);
    }
  };
  return (
    <>
      <DialogHeader
        id="sharing-title"
        title="Create tag"
        onBack={() => {
          if (!pending.current) onBack();
        }}
        onClose={close}
        closeDisabled={saving}
      />
      <Stack
        component="form"
        sx={{ minHeight: 0 }}
        onSubmit={(event) => {
          event.preventDefault();
          void create();
        }}
      >
        <DialogContent sx={{ display: 'grid', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            autoFocus
            label="Tag name"
            value={name}
            disabled={saving}
            error={Boolean(validation)}
            helperText={validation || undefined}
            onChange={(event) => {
              setName(event.target.value);
              setError('');
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={onBack}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !trimmed || Boolean(validation)}
            startIcon={
              saving ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                  aria-label="Creating tag"
                />
              ) : undefined
            }
          >
            Create tag
          </Button>
        </DialogActions>
      </Stack>
    </>
  );
}
